import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';

const AppContext = createContext(null);

// Fallback NAV mirrors backend defaults so the header renders instantly on first paint
// and stays functional even if the site-config request is briefly unavailable.
const FALLBACK_NAV = [
  { key: 'flights', label: 'Flights', route: '/flights' },
  { key: 'hotels', label: 'Hotels', route: '/hotels' },
  { key: 'experiences', label: 'Experiences', route: '/experiences' },
  { key: 'visa', label: 'Visa', route: '/visa' },
  { key: 'store', label: 'Store', route: '/store' },
  { key: 'esim', label: 'eSIM', route: '/esim' },
  { key: 'transfers', label: 'Transfers', route: '/transfers' },
  { key: 'car_rentals', label: 'Car Rentals', route: '/car-rentals' },
  { key: 'insurance', label: 'Insurance', route: '/insurance' },
  { key: 'tours', label: 'Tours', route: '/tours' },
  { key: 'itineraries', label: 'Itineraries', route: '/itineraries' },
  { key: 'creators', label: 'Creators', route: '/creators' },
  { key: 'deals', label: 'Deals', route: '/deals' },
  { key: 'ai_planner', label: 'AI Planner', route: '/ai-travel-planner' },
];

// Holds global currency + last destination context (cross-sell) + the site configuration
// (config-driven NAV / vertical + entity visibility from the Admin source of truth).
export function AppProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem('fd_currency') || 'INR');
  const [destContext, setDestContext] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fd_dest') || 'null'); } catch { return null; }
  });
  const [siteConfig, setSiteConfig] = useState(null);
  const [esimPrompt, setEsimPrompt] = useState(null);
  const promptEsim = (payload) => { if (payload && (payload.country_code || payload.country)) setEsimPrompt({ ...payload, ts: Date.now() }); };
  const closeEsimPrompt = () => setEsimPrompt(null);

  useEffect(() => { localStorage.setItem('fd_currency', currency); }, [currency]);

  useEffect(() => {
    let alive = true;
    api.siteConfig()
      .then(({ data }) => { if (alive) setSiteConfig(data); })
      .catch(() => { /* keep fallback on failure */ });
    return () => { alive = false; };
  }, []);

  const updateDestContext = (ctx) => {
    setDestContext(ctx);
    try { sessionStorage.setItem('fd_dest', JSON.stringify(ctx)); } catch (e) {}
  };

  const navItems = siteConfig?.nav?.length ? siteConfig.nav : FALLBACK_NAV;
  const enabledVerticals = siteConfig?.enabled_verticals || FALLBACK_NAV.map((n) => n.key);
  const visibility = siteConfig?.visibility || {};

  // A vertical route is enabled unless site-config explicitly disables it (fail-open).
  const isRouteEnabled = (route) => {
    if (!siteConfig) return true;
    const all = [...(siteConfig.nav || [])];
    // If the route is not part of the managed nav set at all, allow it (non-vertical pages).
    const managed = FALLBACK_NAV.find((n) => route === n.route || route.startsWith(n.route + '/'));
    if (!managed) return true;
    return all.some((n) => n.route === managed.route);
  };

  const isEntityVisible = (key) => (siteConfig ? visibility[key] !== false : true);

  return (
    <AppContext.Provider value={{
      currency, setCurrency, destContext, updateDestContext,
      siteConfig, navItems, enabledVerticals, visibility,
      isRouteEnabled, isEntityVisible,
      esimPrompt, promptEsim, closeEsimPrompt,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
